import {
  ApplicationConfig,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  APP_INITIALIZER,
  inject,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  provideRouter,
  withInMemoryScrolling,
} from '@angular/router';
import { appRoutes } from './app.routes';
import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { LucideIconsModule } from './icon.config';
import {
  API_BASE_URL,
  authInterceptor,
  baseUrlInterceptor,
  tenantInterceptor,
  UTMService,
  THALIQ_WIDGET_CONFIG,
} from '@mentebonita/core';
import { countryInterceptor } from './shared/interceptors';
import { InitConfigService } from './shared/services/init-config.service';
import { ThaliqEngineService } from './shared/services/thaliq-engine.service';
import { SofiaConsentService } from './shared/services/sofia-consent.service';
import { environment as env } from '../environments/environment';

registerLocaleData(localeEs);

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'enabled',
  anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: THALIQ_WIDGET_CONFIG,
      useValue: env.thaliqWidget,
    },
    {
      provide: API_BASE_URL,
      useValue: env.api,
    },
    provideClientHydration(withEventReplay()),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, inMemoryScrollingFeature),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        baseUrlInterceptor,
        authInterceptor,
        tenantInterceptor,
        countryInterceptor,
      ])
    ),
    importProvidersFrom(LucideIconsModule),
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => inject(InitConfigService).loadConfig(),
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      // Sofía: en la web pública solo con el permiso de la persona; en el portal del paciente, siempre.
      // Primero se lee el interruptor de motor (V3/V4, máx. 2 s) y luego arranca Sofía.
      useFactory: (engine: ThaliqEngineService, sofia: SofiaConsentService) => () =>
        engine.load().then(() => sofia.start()),
      deps: [ThaliqEngineService, SofiaConsentService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => inject(UTMService).initUTMTracking(),
      multi: true,
    },
  ],
};
